import { useState, useEffect, useCallback } from "react";
import {
  fetchEmployeesByDepartment,
  createAssignment,
  updateAssignment,
} from "../../../services/assignmentApi";
import { fetchClientsLookup }                    from "../../../services/clientApi";
import { fetchProjectsLookup, fetchProjectById } from "../../../services/projectApi";
import { fetchDepartmentsLookup } from "../../../services/departmentApi";

/* ── Shared styles ── */
const fieldBase = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1px solid #e2e8f0", fontSize: 13, color: "#334155",
  background: "#fff", outline: "none", fontFamily: "inherit",
  boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s",
};
const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600,
  color: "#475569", marginBottom: 5,
};
const INPUT_BASE = {
  width: "100%", padding: "7px 10px", borderRadius: 7,
  border: "1px solid #e2e8f0", fontSize: 12, color: "#334155",
  background: "#fff", outline: "none", fontFamily: "inherit",
  boxSizing: "border-box", transition: "border-color 0.15s",
};
const TH_STYLE = {
  padding: "10px 12px", fontSize: 11, fontWeight: 700,
  color: "#94a3b8", background: "#f8fafc", letterSpacing: "0.05em",
  textTransform: "uppercase", whiteSpace: "nowrap",
  borderBottom: "1px solid #e2e8f0",
};

/* ── Helpers ── */
function FieldLabel({ children, required }) {
  return (
    <label style={labelStyle}>
      {children}
      {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </label>
  );
}

function SelectField({ label, value, onChange, options, placeholder, required, disabled, error, valueKey = "_id", labelKey }) {
  return (
    <div>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <div style={{ position: "relative" }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            ...fieldBase, appearance: "none", WebkitAppearance: "none",
            paddingRight: 32, cursor: disabled ? "not-allowed" : "pointer",
            background: disabled ? "#f8fafc" : "#fff",
            color: value ? "#334155" : "#94a3b8",
            borderColor: error ? "#fca5a5" : "#e2e8f0",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = error ? "#f87171" : "#6366f1")}
          onBlur={(e)  => (e.currentTarget.style.borderColor = error ? "#fca5a5" : "#e2e8f0")}
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((o) => {
            const val = typeof o === "string" ? o : o[valueKey];
            const lbl = typeof o === "string" ? o : (labelKey ? o[labelKey] : val);
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </select>
        <i className="fa fa-angle-down" style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 13, pointerEvents: "none" }} />
      </div>
      {error && <div style={{ marginTop: 4, fontSize: 11, color: "#dc2626" }}>{error}</div>}
    </div>
  );
}

function hasOverlap(rows) {
  const parsed = rows.map((r) => ({
    start: r.startDate ? new Date(r.startDate) : null,
    end:   r.endDate   ? new Date(r.endDate)   : null,
  }));
  return rows.map((_, i) => {
    if (!parsed[i].start || !parsed[i].end) return false;
    for (let j = 0; j < parsed.length; j++) {
      if (i === j) continue;
      if (!parsed[j].start || !parsed[j].end) continue;
      if (parsed[i].start <= parsed[j].end && parsed[i].end >= parsed[j].start) return true;
    }
    return false;
  });
}

function newDateRow() {
  return { id: Date.now() + Math.random(), startDate: "", endDate: "", notes: "", conflict: false };
}
function newBlock() {
  return { id: Date.now() + Math.random(), employeeId: "", dateRows: [newDateRow()] };
}

/* ── DateRow ── */
function DateRow({ row, onChangeField, onDelete }) {
  const errBorder = row.conflict ? "#fca5a5" : "#e2e8f0";
  return (
    <tr style={{ borderBottom: "1px solid #f1f5f9", background: row.conflict ? "#fff5f5" : "#fff" }}>
      <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
        <input type="date" value={row.startDate}
          onChange={(e) => onChangeField("startDate", e.target.value)}
          style={{ ...INPUT_BASE, borderColor: errBorder, background: row.conflict ? "#fff5f5" : "#fff" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = row.conflict ? "#f87171" : "#6366f1")}
          onBlur={(e)  => (e.currentTarget.style.borderColor = errBorder)}
        />
      </td>
      <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
        <input type="date" value={row.endDate}
          onChange={(e) => onChangeField("endDate", e.target.value)}
          style={{ ...INPUT_BASE, borderColor: errBorder, background: row.conflict ? "#fff5f5" : "#fff" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = row.conflict ? "#f87171" : "#6366f1")}
          onBlur={(e)  => (e.currentTarget.style.borderColor = errBorder)}
        />
      </td>
      <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
        <textarea placeholder="Add a task…" value={row.notes}
          onChange={(e) => onChangeField("notes", e.target.value)}
          rows={2}
          style={{ ...INPUT_BASE, borderColor: errBorder, resize: "vertical" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#6366f1")}
          onBlur={(e)  => (e.currentTarget.style.borderColor = errBorder)}
        />
        {row.conflict && (
          <div style={{ marginTop: 3, fontSize: 10, color: "#dc2626", display: "flex", alignItems: "center", gap: 4 }}>
            <i className="fa fa-exclamation-circle" /> Date conflict detected.
          </div>
        )}
      </td>
      <td style={{ padding: "8px 10px", verticalAlign: "top", textAlign: "center" }}>
        <button onClick={onDelete} title="Delete"
          style={{ border: "none", background: "none", cursor: "pointer", color: "#ef4444", fontSize: 14, padding: 3 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#ef4444")}
        >
          <i className="fa fa-trash" />
        </button>
      </td>
    </tr>
  );
}

/* ── ResourceBlock ── */
function ResourceBlock({ block, index, employees, employeesLoading, onEmployeeChange, onAddDate, onChangeDateField, onDeleteDate, onDeleteBlock, totalBlocks }) {
  const placeholder = employeesLoading
    ? "Loading resources…"
    : employees.length
    ? "Select Resource"
    : "Select a department first";

  const selectedEmp = employees.find((e) => e._id === block.employeeId);
  const deptLabel   = selectedEmp?.departmentChildName || selectedEmp?.departmentName || null;

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", marginBottom: 16, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>Resource {index + 1}</span>
        {totalBlocks > 1 && (
          <button onClick={onDeleteBlock} title="Remove block"
            style={{ width: 30, height: 30, borderRadius: 6, border: "1px solid #fca5a5", background: "#fff", cursor: "pointer", color: "#ef4444", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
          >
            <i className="fa fa-trash" />
          </button>
        )}
      </div>

      {/* Select Resource + Department */}
      <div style={{ padding: "14px 16px 12px", display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: "0 0 55%" }}>
          <SelectField
            label="Select Resource"
            value={block.employeeId}
            onChange={onEmployeeChange}
            options={employees}
            valueKey="_id"
            labelKey="name"
            placeholder={placeholder}
            disabled={employeesLoading || !employees.length}
            required
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Department</label>
          <div style={{ fontSize: 13, minHeight: 36, padding: "8px 10px", background: "#f8fafc", borderRadius: 7, border: "1px solid #e2e8f0", color: deptLabel ? "#1e293b" : "#94a3b8" }}>
            {deptLabel || <span style={{ fontStyle: "italic" }}>—</span>}
          </div>
        </div>
      </div>

      {/* Date table */}
      <div style={{ borderTop: "1px solid #f1f5f9", overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ ...TH_STYLE, width: "28%" }}>Start Date</th>
              <th style={{ ...TH_STYLE, width: "28%" }}>End Date</th>
              <th style={TH_STYLE}>Task</th>
              <th style={{ ...TH_STYLE, width: 80, textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td colSpan={4} style={{ padding: "7px 10px" }}>
                <button onClick={onAddDate}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", border: "1.5px dashed #c7d2fe", background: "#fff", color: "#6366f1", padding: "8px 0", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#eef2ff"; e.currentTarget.style.borderColor = "#6366f1"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff";    e.currentTarget.style.borderColor = "#c7d2fe"; }}
                >
                  <i className="fa fa-plus" style={{ fontSize: 10 }} /> Add Task
                </button>
              </td>
            </tr>
            {block.dateRows.map((row, ri) => (
              <DateRow key={row.id} row={row}
                onChangeField={(field, val) => onChangeDateField(ri, field, val)}
                onDelete={() => onDeleteDate(ri)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   HodAllocateModal
═════════════════════════════════════════════════════ */
const HodAllocateModal = ({ isOpen, onClose, editData, onSaved }) => {
  const [form, setForm]               = useState({ clientId: "", projectId: "", departmentId: "", task: "" });
  const [touched, setTouched]         = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [blocks, setBlocks]           = useState([newBlock()]);
  const [submitted, setSubmitted]     = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [apiError, setApiError]       = useState("");
  const [visible, setVisible]         = useState(false);

  // Data from API
  const [clients,          setClients]          = useState([]);
  const [projects,         setProjects]         = useState([]);
  const [departments,      setDepartments]      = useState([]);
  const [employees,        setEmployees]        = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [descLoading,      setDescLoading]      = useState(false);

  const isEditMode = Boolean(editData);

  // Animate in/out
  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => setVisible(true));
    else setVisible(false);
  }, [isOpen]);

  // ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Load dropdown data once (employees loaded on department change)
  useEffect(() => {
    if (!isOpen) return;
    fetchClientsLookup().then((d) => setClients(d)).catch(() => {});
    fetchProjectsLookup().then((d) => setProjects(d)).catch(() => {});
    fetchDepartmentsLookup().then((d) => setDepartments(d)).catch(() => {});
  }, [isOpen]);

  // Populate form when editing
  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      const nid = (v) => (v && typeof v === "object" ? v._id : v) || "";
      setForm({
        clientId:     nid(editData.clientId),
        projectId:    nid(editData.projectId),
        departmentId: nid(editData.departmentId),
        task:         editData.taskDescription || "",
      });
      const builtBlocks = (editData.resources || []).map((r) => ({
        id: Date.now() + Math.random(),
        employeeId: nid(r.employeeId),
        dateRows: (r.allocations || [newDateRow()]).map((a) => ({
          id:        Date.now() + Math.random(),
          startDate: a.startDate ? a.startDate.slice(0, 10) : "",
          endDate:   a.endDate   ? a.endDate.slice(0, 10)   : "",
          notes:     a.notes || "",
          conflict:  false,
        })),
      }));
      setBlocks(builtBlocks.length ? builtBlocks : [newBlock()]);
    } else {
      setForm({ clientId: "", projectId: "", departmentId: "", task: "" });
      setBlocks([newBlock()]);
    }
    setTouched({});
    setFieldErrors({});
    setApiError("");
    setSubmitted(false);
  }, [isOpen, editData]);

  // Fetch employees whenever departmentId changes
  useEffect(() => {
    if (!form.departmentId) { setEmployees([]); return; }
    setEmployeesLoading(true);
    fetchEmployeesByDepartment(form.departmentId)
      .then((d) => setEmployees(d))
      .catch(() => setEmployees([]))
      .finally(() => setEmployeesLoading(false));
  }, [form.departmentId]);

  // Employees are already scoped to the selected department
  const filteredEmployees = employees;

  // Projects filtered by selected client (inactive projects excluded)
  const filteredProjects = projects.filter((p) => {
    if (p.isActive === false) return false;
    if (!form.clientId) return true;
    const pid = p.clientId && typeof p.clientId === "object" ? p.clientId._id : p.clientId;
    return String(pid) === String(form.clientId);
  });

  const setField = (key) => (val) => {
    if (key === "clientId") {
      // Reset project when client changes
      setForm((f) => ({ ...f, clientId: val, projectId: "", task: "" }));
      setTouched((t) => ({ ...t, clientId: true, projectId: false }));
      setFieldErrors((e) => ({ ...e, clientId: "", projectId: "" }));
      return;
    }
    if (key === "projectId") {
      setForm((f) => ({ ...f, projectId: val, task: "" }));
      setTouched((t) => ({ ...t, projectId: true }));
      setFieldErrors((e) => ({ ...e, projectId: "" }));
      if (val) {
        setDescLoading(true);
        fetchProjectById(val)
          .then((project) => setForm((f) => ({ ...f, task: project?.projectDescription || "" })))
          .catch(() => {})
          .finally(() => setDescLoading(false));
      }
      return;
    }
    if (key === "departmentId") {
      // Clear employee selections in all resource blocks when department changes
      setBlocks((prev) => prev.map((b) => ({ ...b, employeeId: "" })));
      setEmployees([]);
    }
    setForm((f) => ({ ...f, [key]: val }));
    setTouched((t) => ({ ...t, [key]: true }));
    setFieldErrors((e) => ({ ...e, [key]: "" }));
  };

  /* ── Block operations ── */
  const addBlock = () => setBlocks((prev) => [...prev, newBlock()]);

  const deleteBlock = useCallback((blockId) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
  }, []);

  const changeEmployee = useCallback((blockId, val) => {
    setBlocks((prev) => prev.map((b) => b.id === blockId ? { ...b, employeeId: val } : b));
  }, []);

  const addDate = useCallback((blockId) => {
    setBlocks((prev) => prev.map((b) => {
      if (b.id !== blockId) return b;
      return { ...b, dateRows: [...b.dateRows, newDateRow()] };
    }));
  }, []);

  const changeDateField = useCallback((blockId, rowIdx, field, val) => {
    setBlocks((prev) => prev.map((b) => {
      if (b.id !== blockId) return b;
      const next = b.dateRows.map((r, i) => i === rowIdx ? { ...r, [field]: val } : r);
      const conflicts = hasOverlap(next);
      return { ...b, dateRows: next.map((r, i) => ({ ...r, conflict: conflicts[i] })) };
    }));
  }, []);

  const deleteDate = useCallback((blockId, rowIdx) => {
    setBlocks((prev) => prev.map((b) => {
      if (b.id !== blockId) return b;
      const next = b.dateRows.filter((_, i) => i !== rowIdx);
      const conflicts = hasOverlap(next);
      return { ...b, dateRows: next.map((r, i) => ({ ...r, conflict: conflicts[i] })) };
    }));
  }, []);

  /* ── Submit ── */
  const handleSubmit = async () => {
    setSubmitted(true);
    setApiError("");

    const errs = {};
    if (!form.clientId)     errs.clientId     = "Required";
    if (!form.projectId)    errs.projectId    = "Required";
    if (!form.departmentId) errs.departmentId = "Required";
    if (!form.task.trim())  errs.task         = "Required";
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Build API payload
    const resources = blocks
      .filter((b) => b.employeeId)
      .map((b) => ({
        employeeId: b.employeeId,
        allocations: b.dateRows
          .filter((r) => r.startDate && r.endDate)
          .map((r) => ({ startDate: r.startDate, endDate: r.endDate, notes: r.notes })),
      }))
      .filter((r) => r.allocations.length > 0);

    if (resources.length === 0) {
      setApiError("Please select at least one resource with valid date allocations.");
      return;
    }

    const payload = {
      clientId:        form.clientId,
      projectId:       form.projectId,
      departmentId:    form.departmentId,
      taskDescription: form.task,
      resources,
    };

    setSubmitting(true);
    try {
      if (isEditMode) {
        await updateAssignment(editData._id, payload);
      } else {
        await createAssignment(payload);
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setApiError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasConflicts  = blocks.some((b) => b.dateRows.some((r) => r.conflict));
  const cannotSubmit  = submitting || hasConflicts;

  if (!isOpen) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: `rgba(15,23,42,${visible ? 0.45 : 0})`, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px", transition: "background 0.25s", overflowY: "auto" }}
    >
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 25px 60px rgba(0,0,0,0.18)", width: "100%", maxWidth: 880, padding: "28px 32px", position: "relative", transform: visible ? "scale(1) translateY(0)" : "scale(0.95) translateY(16px)", opacity: visible ? 1 : 0, transition: "transform 0.25s cubic-bezier(.34,1.36,.64,1), opacity 0.2s", maxHeight: "90vh", overflowY: "auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <h4 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1e293b" }}>{isEditMode ? "Edit Job" : "Add Job"}</h4>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>Assign resources and define their allocation schedule</p>
          </div>
          <button onClick={onClose}
            style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b", fontSize: 16 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#dc2626"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#64748b"; }}
          >
            <i className="fa fa-times" />
          </button>
        </div>

        <div style={{ borderTop: "1px solid #f1f5f9", marginBottom: 24 }} />

        {/* API error */}
        {apiError && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#dc2626", fontWeight: 500 }}>
            <i className="fa fa-exclamation-triangle" />
            {apiError}
          </div>
        )}

        {/* Top form fields */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 20px", marginBottom: 24 }}>
          <SelectField label="Select Client"     value={form.clientId}     onChange={setField("clientId")}     options={clients}     valueKey="_id" labelKey="clientName"     required error={(submitted || touched.clientId)     ? fieldErrors.clientId     : ""} />
          <SelectField label="Select Project"    value={form.projectId}    onChange={setField("projectId")}    options={filteredProjects} valueKey="_id" labelKey="projectName"    required error={(submitted || touched.projectId)    ? fieldErrors.projectId    : ""} placeholder={form.clientId ? "Select Project" : "Select a client first"} />
          <SelectField label="Select Skill" value={form.departmentId} onChange={setField("departmentId")} options={departments} valueKey="_id" labelKey="departmentName" required error={(submitted || touched.departmentId) ? fieldErrors.departmentId : ""} />
          <div>
            <FieldLabel required>Project Description</FieldLabel>
            <textarea rows={2}
              placeholder={descLoading ? "Loading description…" : "Select a project to load its description…"}
              value={form.task}
              disabled={descLoading}
              onChange={(e) => setField("task")(e.target.value)}
              style={{ ...fieldBase, resize: "vertical", lineHeight: 1.6, borderColor: (submitted || touched.task) && fieldErrors.task ? "#fca5a5" : "#e2e8f0", background: descLoading ? "#f8fafc" : "#fff" }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#6366f1")}
              onBlur={(e)  => (e.currentTarget.style.borderColor = (submitted || touched.task) && fieldErrors.task ? "#fca5a5" : "#e2e8f0")}
            />
            {(submitted || touched.task) && fieldErrors.task && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#dc2626" }}>{fieldErrors.task}</div>
            )}
          </div>
        </div>

        {/* Date Allocation section */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <h6 style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 700, color: "#334155" }}>Date Allocation</h6>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>Define the allocation schedule per resource.</p>
            </div>
            <button onClick={addBlock}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1.5px dashed #6366f1", background: "#fff", color: "#6366f1", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
            >
              <i className="fa fa-plus" style={{ fontSize: 10 }} /> Add More Resource
            </button>
          </div>

          {hasConflicts && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc2626", fontWeight: 500 }}>
              <i className="fa fa-exclamation-triangle" />
              Resource already booked for selected dates. Please resolve conflicts.
            </div>
          )}

          {blocks.map((block, idx) => (
            <ResourceBlock
              key={block.id}
              block={block}
              index={idx}
              employees={filteredEmployees}
              employeesLoading={employeesLoading}
              totalBlocks={blocks.length}
              onEmployeeChange={(val) => changeEmployee(block.id, val)}
              onAddDate={() => addDate(block.id)}
              onChangeDateField={(ri, field, val) => changeDateField(block.id, ri, field, val)}
              onDeleteDate={(ri) => deleteDate(block.id, ri)}
              onDeleteBlock={() => deleteBlock(block.id)}
            />
          ))}
        </div>

        <div style={{ borderTop: "1px solid #f1f5f9", marginBottom: 20 }} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
          <button onClick={onClose}
            style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={cannotSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 22px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontSize: 13, fontWeight: 700, cursor: cannotSubmit ? "not-allowed" : "pointer", opacity: cannotSubmit ? 0.75 : 1 }}
          >
            {submitting
              ? <><i className="fa fa-spinner fa-spin" /> {isEditMode ? "Saving…" : "Allocating…"}</>
              : <><i className="fa fa-check" style={{ fontSize: 11 }} /> {isEditMode ? "Save Changes" : "Add Job"}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HodAllocateModal;